import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { WebService } from '../services/web/web.service';

export type LaunchDateRange = 'last_7_days' | 'last_30_days' | 'current_month' | 'current_year' | 'custom' | null;
export type LaunchStatus = 'launched' | 'not-launched';

export interface ServiceLaunchRecord {
  companyId: string;
  companyName: string;
  serviceId: string;
  serviceName: string;
  launchedAt: Date | null;
  launchedByName: string;
  launchedByEmail: string;
  status: LaunchStatus;
}

export interface ServiceLaunchReportFilters {
  dateRange: LaunchDateRange;
  startDate?: Date | null;
  endDate?: Date | null;
  companyId?: string | null;
  serviceId?: string | null;
  status?: LaunchStatus | null;
  pageIndex: number;
  pageSize: number;
}

export interface ServiceLaunchReportPage {
  data: ServiceLaunchRecord[];
  total: number;
  pageIndex: number;
  pageSize: number;
}

export interface ServiceLaunchReportApiQuery {
  dateRange?: 'custom';
  startDate?: string;
  endDate?: string;
  partnerId?: string;
  serviceId?: string;
  status?: LaunchStatus;
  pageIndex: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class ServiceLaunchReportService {
  constructor(private webService: WebService) {}

  getCompanies(): Observable<any[]> {
    const params = this.webService.buildRequestParams()
      .set('parents', 'false')
      .set('parent', '-1');

    return this.webService.get(`${this.webService.HOST}/partner/user`, params).pipe(
      map((response) => response?.result ?? response ?? [])
    );
  }

  getServices(partnerId: string): Observable<any[]> {
    return this.webService
      .get(`${this.webService.HOST}/partner/${partnerId}/services`)
      .pipe(map((response) => response?.result ?? response ?? []));
  }

  getPage(filters: ServiceLaunchReportFilters): Observable<ServiceLaunchReportPage> {
    const query = this.buildApiQuery(filters);
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });

    return this.webService
      .get(`${this.webService.HOST}/api/v1/services/launches`, params)
      .pipe(map((response) => this.toPage(response, filters)));
  }

  /** Maps the report filters to the launches endpoint contract. */
  buildApiQuery(filters: ServiceLaunchReportFilters): ServiceLaunchReportApiQuery {
    return {
      ...this.getDateQuery(filters),
      ...(filters.companyId ? { partnerId: filters.companyId } : {}),
      ...(filters.serviceId ? { serviceId: filters.serviceId } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      pageIndex: filters.pageIndex,
      pageSize: filters.pageSize,
    };
  }

  private getDateQuery(filters: ServiceLaunchReportFilters): Pick<ServiceLaunchReportApiQuery, 'dateRange' | 'startDate' | 'endDate'> {
    if (!filters.dateRange) return {};

    const today = this.startOfDay(new Date());
    let start: Date | null = filters.startDate ?? null;
    let end: Date | null = filters.endDate ?? null;

    switch (filters.dateRange) {
      case 'last_7_days':
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        end = today;
        break;
      case 'last_30_days':
        start = new Date(today);
        start.setDate(start.getDate() - 29);
        end = today;
        break;
      case 'current_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'current_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'custom':
        break;
    }

    if (!start || !end) return {};
    return {
      dateRange: 'custom',
      startDate: this.formatDate(start),
      endDate: this.formatDate(end),
    };
  }

  private toPage(response: any, filters: ServiceLaunchReportFilters): ServiceLaunchReportPage {
    const payload = response?.result ?? response;
    const rows = Array.isArray(payload) ? payload : (payload?.data ?? payload?.results ?? payload?.items ?? []);
    const pager = response?.pager ?? payload?.pager ?? payload?.pagination ?? {};

    const data = rows.map((row: any) => this.toRecord(row));
    return {
      data,
      total: pager.totalEntities ?? payload?.total ?? pager.total ?? rows.length,
      pageIndex: pager.pageIndex ?? filters.pageIndex,
      pageSize: pager.pageSize ?? filters.pageSize,
    };
  }

  private toRecord(row: any): ServiceLaunchRecord {
    const company = row.company ?? row.partner ?? {};
    const service = row.service ?? {};
    const launchedBy = row.launched_by ?? row.launchedBy ?? {};
    const launchedAt = row.launched_at ?? row.launchedAt ?? null;
    return {
      companyId: String(row.partnerId ?? row.company_id ?? row.companyId ?? company.id ?? ''),
      companyName: row.partnerName ?? row.company_name ?? row.companyName ?? company.name ?? '',
      serviceId: String(row.service_id ?? row.serviceId ?? service.id ?? ''),
      serviceName: row.service_name ?? row.serviceName ?? service.name ?? '',
      launchedAt: launchedAt ? new Date(launchedAt) : null,
      launchedByName: row.launchedByName ?? row.launched_by_name ?? launchedBy.name ?? '',
      launchedByEmail: row.launched_by_email ?? row.launchedByEmail ?? launchedBy.email ?? '',
      status: (row.status === 'not-launched' || row.status === 'not_launched' || !launchedAt) ? 'not-launched' : 'launched',
    };
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

}
